import React from "react";
import { useShallow } from "zustand/react/shallow";
import {
  Grid,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
} from "@mui/material";
import Autocomplete, { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { Marker } from "../api/transformers/marker";
import ProfileContainer from "./ProfileContainer";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import useInvestorKycStore from "../zstore/investorKyc";
import useBoolean from "../hooks/useBoolean";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { ENTITY_TYPE_CATEGORY } from "../utils/investor";
import { kycGlobalCheck, prepareKycCheckContext } from "../utils/kycMarkerMaintain";
import useMessage from "../hooks/useMessage";
import restUtils from "core/utils/rest";

type NonIndividualSummaryProfileProps = {
  personId: string;
  markers: Marker[];
};

const NonIndividualSummaryProfile = ({ personId, markers }: NonIndividualSummaryProfileProps) => {
  const [categoryOptions, setCategoryOptions] = React.useState<Array<{ label: string; options: Array<{ label: string; value: string; guidanceText?: string; categoryCode: string }> }>>([]);
  const [optionsLoading, { setTrue: startLoadingOptions, setFalse: finishLoadingOptions }] = useBoolean();
  const [guidanceModalOpen, { setTrue: openGuidanceModal, setFalse: closeGuidanceModal }] = useBoolean();
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] = useBoolean();
  const [typeEditing, { setTrue: startEditing, setFalse: finishEditing }] = useBoolean();
  const message = useMessage();
  const { investorMarkers, refetchInvestorMarkers } = useInvestorMarkerStore(
    useShallow((state) => ({ investorMarkers: state.data, refetchInvestorMarkers: state.fetchData }))
  );
  const [guidanceText, setGuidanceText] = React.useState("");
  const { amlDetails, amlDetailsLoading } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetails: state.data,
      amlDetailsLoading: state.loading
    }))
  );
  const entityType = amlDetails?.entityType ?? "";
  const typeCategory = React.useMemo(() => {
    const code = (entityType || "").toUpperCase();
    if (code.includes("COR")) return ENTITY_TYPE_CATEGORY.CORPORATE;
    if (code.startsWith("IND") || code.includes("INDIVIDUAL")) return ENTITY_TYPE_CATEGORY.INDIVIDUAL;
    return ENTITY_TYPE_CATEGORY.TRUST;
  }, [entityType]);
  const trustInvestor = Boolean(amlDetails) && typeCategory === ENTITY_TYPE_CATEGORY.TRUST;
  const corporateInvestor = Boolean(amlDetails) && typeCategory === ENTITY_TYPE_CATEGORY.CORPORATE;
  const individualInvestor = Boolean(amlDetails) && typeCategory === ENTITY_TYPE_CATEGORY.INDIVIDUAL;
  const { data: kycData, isLoading: kycLoading, refetch: refetchKyc } = useInvestorKycStore(personId);
  const [currentSubCategoryCode, setCurrentCategoryCode] = React.useState<string | undefined>();
  React.useEffect(() => {
    if (amlDetails && typeCategory !== ENTITY_TYPE_CATEGORY.INDIVIDUAL) {
      fetchCategoryOptions(typeCategory);
    }
  }, [amlDetails, typeCategory]);
  React.useEffect(() => {
    if (kycData) {
      setCurrentCategoryCode(kycData.subTypeCode);
    }
  }, [kycData]);
  React.useEffect(() => {
    if (currentSubCategoryCode) {
      const targetOption = categoryOptions
        .flatMap((el) => el.options)
        .find((el) => el.value === currentSubCategoryCode);
      setGuidanceText(targetOption?.guidanceText ?? "");
    }
  }, [currentSubCategoryCode, categoryOptions]);
  // Build flat options for Autocomplete with grouping (hook must precede any early return)
  const autoOptions = React.useMemo(
    () => categoryOptions.flatMap((group) =>
      group.options.map((opt) => ({ ...opt, group: group.label }))
    ),
    [categoryOptions]
  );
  const fetchCategoryOptions = (formType: string) => {
    startLoadingOptions();
    return restUtils
      .call(
        "PortalApi",
        `hubwise-api/system/configuration/entity/${formType}/category-details`,
        "GET"
      )
      .then((response) => {
        const list: RawCategoryRow[] = Array.isArray(response)
          ? (response as RawCategoryRow[])
          : Array.isArray((response as any)?.payload)
            ? ((response as any).payload as RawCategoryRow[])
            : [];
        const filtered = list.filter((row) => !row.fromType || row.fromType === formType);
        setCategoryOptions(filtered.map(toOptions));
      })
      .catch((err: unknown) => {
        console.error(err);
        message.error("Failed to load category options.");
      })
      .finally(finishLoadingOptions);
  };
  // [HWAP-5841] For corporate and trust. And not allow edit (Requirement change).
  if (amlDetailsLoading || (!corporateInvestor && !trustInvestor)) {
    return null;
  }
  const prefix = trustInvestor ? "Trust" : "Corporate";
  const handleSubCategoryChange = (value: string | null) => {
    setCurrentCategoryCode(value ?? undefined);
    finishEditing();
  };
  const handleCancel = () => {
    setCurrentCategoryCode(kycData?.subTypeCode);
  };
  const handleSave = () => {
    startSubmitting();
    const targetOption = categoryOptions
      .flatMap((el) => el.options)
      .find((el) => el.value === currentSubCategoryCode);
    if (!targetOption) {
      message.error("Please select a valid type.");
      finishSubmitting();
      return;
    }
    const payload = {
      ...kycData,
      categoryCode: targetOption.categoryCode,
      subTypeCode: targetOption.value,
    };
    const investorId = personId;
    restUtils.call(
      "PortalApi",
      `hubwise-api/ims/investor/${investorId}/kyc`,
      "POST",
      payload
    ).then(async () => {
      message.success(`${prefix} type update successfully.`);
      await kycHealthCheck(targetOption.categoryCode, targetOption.value);
      refetchKyc();
      refetchInvestorMarkers(personId, markers);
    }).catch((err: unknown) => {
      console.error(err);
      message.error(`Failed to update ${prefix} type.`);
    }).finally(() => {
      finishSubmitting();
    });
  };

  const kycHealthCheck = async (categoryCode: string, subCategoryCode: string) => {
    try {
      const context = await prepareKycCheckContext(personId, {
        investorKyc: kycData,
        existingMarkers: investorMarkers ?? []
      });
      return kycGlobalCheck(personId, { categoryCode, subCategoryCode }, context);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  return (
    <ProfileContainer id="non-individual-summary" title={`${prefix} Summary`} loading={kycLoading || optionsLoading}>
      <Grid container spacing={2}>
        <Grid item md={6} sm={12} xs={12}>
          <Stack spacing={2} width={1}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontStyle: "italic" }}>{prefix} Type: </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                {typeEditing ? (
                  <Autocomplete<{ group: string; label: string; value: string; guidanceText?: string; categoryCode: string }, false, false, false>
                    autoHighlight
                    autoSelect
                    openOnFocus
                    loading={optionsLoading}
                    value={autoOptions.find((o) => o.value === currentSubCategoryCode) ?? null}
                    onChange={(
                      _event: React.SyntheticEvent,
                      value: { group: string; label: string; value: string; guidanceText?: string; categoryCode: string } | null,
                      _reason: AutocompleteChangeReason
                    ) => handleSubCategoryChange(value?.value ?? null)}
                    options={autoOptions}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Type" autoFocus />
                    )}
                    onBlur={finishEditing}
                  />
                ) : (
                  <Typography>
                    {getSubCategoryDesc(categoryOptions, currentSubCategoryCode) || "Not Specified"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Stack>
        </Grid>

        {guidanceText && (
          <Grid item md={6} sm={12} xs={12}>
            <Box display="flex" flexDirection="column" gap={1} width={1}>
              <Typography sx={{ fontStyle: "italic", fontWeight: "bold" }}>
                {prefix} Matrix Guidance:
              </Typography>
              <Guidance text={guidanceText} onExpand={openGuidanceModal} />
            </Box>
          </Grid>
        )}
      </Grid>

      {kycData?.subTypeCode !== currentSubCategoryCode && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} width={1} mt={3}>
          <Button variant="outlined" disabled={submitting} onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" disabled={submitting} onClick={handleSave}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </Stack>
      )}

      <Dialog open={guidanceModalOpen} onClose={closeGuidanceModal} fullWidth maxWidth="sm">
        <DialogTitle>{`${prefix} Matrix Guidance`}</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>{guidanceText}</Typography>
        </DialogContent>
      </Dialog>
    </ProfileContainer>
  );
};

type GuidanceProps = { text: string; onExpand: () => void };
const Guidance: React.FC<GuidanceProps> = ({ text, onExpand }) => {
  const formatTextWithLineBreaks = (content: string) => {
    return content
      .replace(/\r\n|\r/g, "\n")
      .split("\n")
      .map((line: string, index: number) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ));
  };
  return (
    <Box>
      <Box
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 6,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        <Typography component="div" sx={{ whiteSpace: "pre-wrap" }}>
          {formatTextWithLineBreaks(text)}
        </Typography>
      </Box>
      <Box mt={1}>
        <Button size="small" onClick={onExpand}>Show all</Button>
      </Box>
    </Box>
  );
};

type RawCategoryRow = {
  categoryDesc: string;
  categoryCode: string;
  fromType?: string;
  subTypeCodes: Array<{ subTypeDesc: string; subTypeCode: string; guidanceText?: string; activeStatus: string }>;
};
function toOptions(row: RawCategoryRow) {
  return {
    label: row.categoryDesc,
    options: row.subTypeCodes
      .filter((el: RawCategoryRow["subTypeCodes"][number]) => el.activeStatus === "Y")
      .map((el: RawCategoryRow["subTypeCodes"][number]) => ({
        label: el.subTypeDesc,
        value: el.subTypeCode,
        guidanceText: el.guidanceText,
        categoryCode: row.categoryCode
      }))
  };
}

function getSubCategoryDesc(
  options: Array<{ label: string; options: Array<{ label: string; value: string }> }>,
  code: string | undefined
) {
  return options.flatMap((el) => el.options).find((el) => el.value === code)?.label;
}

export default NonIndividualSummaryProfile;
